import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableLoading
} from "@/components/ui/table";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  CaretSortIcon,
} from "@radix-ui/react-icons";
import { CircleXIcon, MoveRightIcon } from "lucide-react";
import { Route as overviewRoute } from "@/routes/(pharma)/_layout/overview/sales_history/index";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { useNavigate } from "@tanstack/react-router";
import { AutoComplete, Option } from "@/components/ui/autocomplete";
import { DatePickerWithRange } from "@/components/ui/date-picker";
import { DateRange } from "react-day-picker";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { SaleStatus } from "@/gen/sales/common_pb";
import { useSalesHistoryData } from "../-hooks/useSalesHistoryData";

const Component = () => {
  const { data, isLoading, error, isFetching } = useSalesHistoryData();
  const users = data?.users || [];
  const sales_history = data?.sales_history || { sales: [], totalCount: 0 };
  const searchParams = overviewRoute.useSearch();
  const { page, pageSize, userID, startDate, endDate, type } = searchParams;
  const navigate = useNavigate({ from: "/overview/sales_history" });
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_at");
  const [userName, setUserName] = useState<string>("");
  const [searchValue, setSearchValue] = useState<Option | undefined>(undefined);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from:
      startDate && startDate > 0 ? dayjs.unix(startDate).toDate() : undefined,
    to: endDate && endDate > 0 ? dayjs.unix(endDate).toDate() : undefined,
  });

  const handleDateChange = useCallback(
    (newDateRange: DateRange | undefined) => {
      setDateRange(newDateRange);
    },
    []
  );

  const handleSubmit = useCallback(() => {
    if (dateRange?.from && dateRange?.to) {
      navigate({
        search: {
          page: 1,
          pageSize: pageSize,
          userID: userID,
          startDate: Math.round(dateRange.from.getTime() / 1000),
          endDate: Math.round(dateRange.to.getTime() / 1000),
          type: type,
        },
      });
    }
  }, [navigate, dateRange, page, pageSize, userID, startDate, endDate, type]);

  const sortedData = sales_history?.sales ? sales_history.sales.slice().sort((a: { saleValues: { createdAt: { toDate: () => string | number | Date; }; }; }, b: { saleValues: { createdAt: { toDate: () => string | number | Date; }; }; }) => {
    if (orderBy === "created_at") {
      const dateA = new Date(a.saleValues?.createdAt?.toDate()!).getTime();
      const dateB = new Date(b.saleValues?.createdAt?.toDate()!).getTime();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  }) : [];

  const columns = [
    { key: "invoice_id", label: "Invoice ID" },
    { key: "created_at", label: "Created At" },
    { key: "patient_name", label: "Patient Name" },
    { key: "emirates_id", label: "Emirates ID" },
    { key: "phone_number", label: "Phone Number" },
    { key: "billed_amount", label: "Billed Amount" },
    { key: "status", label: "Status" },
    { key: "actions", label: "" },
  ];

  const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

  const totalPages = sales_history?.totalCount && sales_history.totalCount > 0 ? Math.ceil(sales_history.totalCount / pageSize) : 1;

  const filteredDropDownContent = useMemo(
    () =>
      users.filter((user) => {
        return user.fullName?.trim() !== "";
      })
    , [users]
  );

  const options: Option[] = useMemo(
    () => filteredDropDownContent.map((user) => ({
      value: user.userId.toString(),
      label: `${user.fullName} (${user.username})`,
    })), [filteredDropDownContent]);

  useEffect(() => {
    const selectedUser = filteredDropDownContent.find((u) => u.userId === userID);
    setUserName(
      selectedUser
        ? `${selectedUser.fullName} (${selectedUser.username})`
        : ""
    );
    setSearchValue(
      selectedUser
        ? {
          label: `${selectedUser.fullName} (${selectedUser.username})`,
          value: selectedUser.userId.toString(),
        }
        : { label: "", value: "" }
    );
  }, [filteredDropDownContent]);

  const resetSearch = () => {
    navigate({
      search: {
        page: 1,
        pageSize: pageSize,
        userID: undefined,
        type: undefined,
      },
    });
  };

  const clearDate = () => {
    setDateRange({ from: undefined, to: undefined });
    navigate({
      search: {
        ...searchParams,
        page: 1,
        startDate: undefined,
        endDate: undefined,
      },
    });
  };

  // TableCell and TableHead wrappers for consistent styling
  const StyledTableCell = ({ className = "", ...props }) => (
    <TableCell className={cn(tableCellClass, "py-0", className)} {...props} />
  );
  const StyledTableHead = ({ className = "", ...props }) => (
    <TableHead className={cn(tableHeadClass, className)} {...props} />
  );

  return (
    <div className="">
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/overview">Overview</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Sales History</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="px-4 py-2 h-10 ml-20">
          <Tabs defaultValue={type}>
            <TabsList>
              {["", "COMPLETED", "PARTIALLY_REFUNDED", "FULLY_REFUNDED", "VOID", "ON_HOLD"].map((type) => (
                <TabsTrigger
                  key={type}
                  value={type}
                  onClick={() => {
                    navigate({
                      search: {
                        ...searchParams,
                        type,
                        page: 1,
                      },
                    });
                  }}
                >
                  {type.replace(
                    /_/g,
                    " "
                  ).replace(/(^\w|\s\w)/g, (m) => m.toUpperCase()
                  ) || "ALL"}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center justify-center gap-x-4 h-8">
          <div className="flex items-center border border-input rounded-md bg-white">
            <DatePickerWithRange
              date={dateRange}
              setDate={handleDateChange}
              onSubmit={handleSubmit}
              onClear={clearDate}
              disabledAfter={dayjs().toDate()}
              className="border-none h-8 w-full"
            />
            {dateRange?.from && dateRange?.to && (
              <Button
                className="border-0 h-auto p-2"
                variant="outline"
                onClick={clearDate}
              >
                <CircleXIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-center w-72 border border-input rounded-md bg-white">
            <AutoComplete
              options={options}
              placeholder="Filter by sales user"
              emptyMessage="No user found"
              input={userName}
              onInputChanged={(value) => setUserName(value)}
              onValueChange={(value) => {
                navigate({
                  search: {
                    page: 1,
                    pageSize: pageSize,
                    type: "",
                    userID: parseInt(value.value),
                  },
                });
              }}
              value={searchValue}
              className="flex-1 border-0 "
            />
            {userName && (
              <Button
                className="border-0 h-auto p-2"
                variant="outline"
                onClick={resetSearch}
              >
                <CircleXIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
      <hr className="my-4" />
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="mt-4 border rounded-lg bg-white overflow-hidden">
          {isLoading ? (
            <TableLoading columns={columns} visibleColumns={visibleColumns} />
          ) : !sales_history?.sales || sales_history.sales.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-gray-500">No entries found</div>
            </div>
          ) : (
            <>
              <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <StyledTableHead>Invoice ID</StyledTableHead>
                <StyledTableHead
                  onClick={() => handleRequestSort("created_at")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="flex items-center">
                    <span>Created At</span>
                    {orderBy === "created_at" && (
                      <CaretSortIcon
                        className={`ml-2 h-4 w-4 ${order === "asc" ? "rotate-180" : ""}`}
                      />
                    )}
                  </div>
                </StyledTableHead>
                <StyledTableHead>Patient Name</StyledTableHead>
                <StyledTableHead>Emirates ID</StyledTableHead>
                <StyledTableHead>Phone Number</StyledTableHead>
                {/* <StyledTableHead>ERX Number</StyledTableHead> */}
                {/* <StyledTableHead>Payment Type</StyledTableHead> */}
                <StyledTableHead>Billed Amount</StyledTableHead>
                <StyledTableHead>Status</StyledTableHead>
                <StyledTableHead></StyledTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((sale: { receiptNumber: unknown; saleValues: { createdAt: { toDate: () => string | number | dayjs.Dayjs | Date | null | undefined; }; grandTotalAmount: number; status: SaleStatus; }; isValidPatient: any; patient: { name: unknown; govtIdentifier: unknown; phone: unknown; }; saleId: any; }, index: React.Key | null | undefined) => (
                <TableRow key={index}>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>{sale.receiptNumber}</StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {dayjs(sale.saleValues?.createdAt?.toDate()).format("DD/MM/YYYY HH:mm A")}
                  </StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {sale.isValidPatient ? sale.patient?.name : "OTC"}
                  </StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {sale.isValidPatient ? sale.patient?.govtIdentifier : "OTC"}
                  </StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {sale.isValidPatient ? sale.patient?.phone : "OTC"}
                  </StyledTableCell>
                  {/* <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {sale.isva
                      ? sale.insurance_details.erx_number
                      : "Not Available"}
                  </StyledTableCell> */}
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    {convertToCurrencyRounded(
                      sale.saleValues?.grandTotalAmount!
                    )}
                  </StyledTableCell>
                  {/* <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>{sale.sale_status.refunded ? "Yes" : "No"}</StyledTableCell> */}
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>{
                      Object.keys(SaleStatus).find(
                        (key) => SaleStatus[key as keyof typeof SaleStatus] === sale?.saleValues?.status
                      ) || "Unknown"
                    }</StyledTableCell>
                  <StyledTableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                    <Button
                      variant="link"
                      onClick={() => {
                        navigate({
                          to: `/overview/sales_history/${sale.saleId}`,
                          search: searchParams,
                        });
                      }}
                    >
                      <MoveRightIcon className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
          )}
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{sales_history?.sales && sales_history.sales.length > 0 ? sales_history.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: 1
                    }
                  });
                }}
                disabled={page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: page - 1
                    }
                  });
                }}
                disabled={page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {

                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (page > 3) pages.push('...');
                    for (let i = Math.max(2, page - 1); i <= Math.min(page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((p, idx) => (
                  p === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={p}
                        active={page === p}
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: typeof p === 'number' ? p : page
                            }
                          });
                        }}
                      >
                        <span className="text-sm">{p}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: page + 1
                    }
                  });
                }}
                disabled={page >= totalPages}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: totalPages
                    }
                  });
                }}
                disabled={page >= totalPages}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Component;
